import { useTranslation } from 'react-i18next';
import { Briefcase, Calendar, Users, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FlowStep {
  icon: typeof Briefcase;
  labelKey: string;
  descriptionKey: string;
  color: string;
  bgColor: string;
}

const FLOW_STEPS: FlowStep[] = [
  {
    icon: Briefcase,
    labelKey: 'availability.wizard.flow.services',
    descriptionKey: 'availability.wizard.flow.servicesDesc',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    icon: Calendar,
    labelKey: 'availability.wizard.flow.availability',
    descriptionKey: 'availability.wizard.flow.availabilityDesc',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  {
    icon: Users,
    labelKey: 'availability.wizard.flow.bookings',
    descriptionKey: 'availability.wizard.flow.bookingsDesc',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
];

interface FlowDiagramProps {
  className?: string;
  compact?: boolean;
}

export function FlowDiagram({ className, compact = false }: FlowDiagramProps) {
  const { t } = useTranslation('staff');

  if (compact) {
    // Horizontal compact version for smaller spaces
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        {FLOW_STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.labelKey} className="flex items-center gap-2">
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  step.bgColor
                )}
              >
                <Icon className={cn('w-5 h-5', step.color)} />
              </div>
              {index < FLOW_STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full vertical version for welcome step
  return (
    <div className={cn('space-y-4', className)}>
      {FLOW_STEPS.map((step, index) => {
        const Icon = step.icon;
        return (
          <div key={step.labelKey} className="relative">
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  'w-12 h-12 rounded-lg flex items-center justify-center shrink-0',
                  step.bgColor
                )}
              >
                <Icon className={cn('w-6 h-6', step.color)} />
              </div>
              <div className="flex-1 pt-1">
                <p className="font-semibold text-gray-900">
                  {t(step.labelKey, step.labelKey.split('.').pop())}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t(step.descriptionKey, step.descriptionKey.split('.').pop())}
                </p>
              </div>
            </div>
            {/* Connector line */}
            {index < FLOW_STEPS.length - 1 && (
              <div className="absolute left-6 top-14 w-0.5 h-4 bg-gray-200" />
            )}
          </div>
        );
      })}
    </div>
  );
}
